import { Component } from "react";

import { RefreshCw, TriangleAlert } from "lucide-react";
import PropTypes from "prop-types";

/**
 * Scopes a render error to the subtree it occurs in instead of letting it
 * bubble up to the app-level ErrorBoundary (which blanks the entire screen).
 * Wrap an individual widget, dashboard section, or route's content with this
 * so a bug in one place doesn't take the whole page down.
 */
class ComponentErrorBoundary extends Component {
    constructor (props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError () {
        return { hasError: true };
    }

    componentDidCatch (error, errorInfo) {
        console.error(`Error rendering "${this.props.name || "component"}":`, error, errorInfo);
    }

    handleRetry () {
        this.setState({ hasError: false });
    }

    render () {
        const { hasError } = this.state;
        const { children, name, fallbackMessage, className = "" } = this.props;

        if (!hasError) return children;

        return (
            <div className={`flex flex-col items-center justify-center gap-2 rounded-lg border border-danger/20 bg-danger/5 p-6 text-center ${className}`}>
                <TriangleAlert className="h-5 w-5 text-danger" />
                <p className="text-sm text-muted">
                    {fallbackMessage || (name ? `Something went wrong loading "${name}".` : "Something went wrong loading this section.")}
                </p>
                <button
                    type="button"
                    onClick={() => this.handleRetry()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                    <RefreshCw className="h-3 w-3" />
                    Try again
                </button>
            </div>
        );
    }
}

ComponentErrorBoundary.propTypes = {
    children       : PropTypes.node.isRequired,
    name           : PropTypes.string,
    fallbackMessage: PropTypes.string,
    className      : PropTypes.string
};

export default ComponentErrorBoundary;
